/**
 * Manual/Handbook React Query Hooks
 *
 * Custom hooks for manual/handbook functionality.
 * Story 7-2, Story 2-3 (search), Story 6-4 (cache configuration)
 */

import { useQuery } from '@tanstack/react-query';
import { useDeferredValue } from 'react';
import { manualApi } from '../../lib/manualApi';

/**
 * Manual query defaults for cache configuration
 * Story 6-4: staleTime 1 hour, gcTime 24 hours
 */
export const manualQueryDefaults = {
  staleTime: 1000 * 60 * 60, // 1 hour
  gcTime: 1000 * 60 * 60 * 24, // 24 hours
};

// Query keys factory pattern (per project-context.md)
export const manualKeys = {
  all: ['manual'] as const,
  sections: () => [...manualKeys.all, 'sections'] as const,
  section: (slug: string) => [...manualKeys.all, 'section', slug] as const,
  tree: () => [...manualKeys.all, 'tree'] as const,
  changelog: () => [...manualKeys.all, 'changelog'] as const,
  search: (query: string) => [...manualKeys.all, 'search', query] as const,
  /** Story 5.2: Placeholder query keys */
  placeholder: (type: string) => [...manualKeys.all, 'placeholder', type] as const,
};

/**
 * Hook to fetch all manual sections for current user
 * Story 6-4: Uses 1 hour staleTime
 */
export function useManual() {
  return useQuery({
    queryKey: manualKeys.sections(),
    queryFn: () => manualApi.fetchManual(),
    ...manualQueryDefaults,
  });
}

/**
 * Hook to fetch single section by slug
 * Story 6-4: Uses 1 hour staleTime
 */
export function useManualSection(slug: string) {
  return useQuery({
    queryKey: manualKeys.section(slug),
    queryFn: () => manualApi.fetchSection(slug),
    enabled: !!slug,
    ...manualQueryDefaults,
  });
}

/**
 * Hook to fetch version history/changelog
 */
export function useChangelog() {
  return useQuery({
    queryKey: manualKeys.changelog(),
    queryFn: () => manualApi.fetchChangelog(),
    ...manualQueryDefaults,
  });
}

/**
 * Hook to fetch hierarchical tree of manual sections
 * Used for tree navigation sidebar component
 * Story 1-3: ManualTreeSidebar
 * Story 6-4: Uses 1 hour staleTime, 24 hour gcTime
 */
export function useManualTree() {
  return useQuery({
    queryKey: manualKeys.tree(),
    queryFn: () => manualApi.fetchTree(),
    ...manualQueryDefaults,
  });
}

/** Minimum characters required before search is triggered */
export const MIN_SEARCH_LENGTH = 2;

/**
 * Hook to search manual sections with FTS5 full-text search.
 * Uses useDeferredValue for debouncing (React 18 concurrent feature).
 *
 * Story 2-3: ManualSearch Component
 *
 * @param query - Search query string
 * @returns Query result with search results and loading state
 */
export function useManualSearch(query: string) {
  const deferredQuery = useDeferredValue(query);

  return useQuery({
    queryKey: manualKeys.search(deferredQuery),
    queryFn: () => manualApi.search(deferredQuery),
    enabled: deferredQuery.length >= MIN_SEARCH_LENGTH,
    staleTime: 1000 * 60 * 5, // 5 minutes - search results can change
    gcTime: 1000 * 60 * 30, // 30 minutes - keep in cache for faster re-search
  });
}
